import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import useAuth from '../../../auth/useAuth';

import Layout from '../../UI/introGameLayout/layout';

import ProcessReviewSession from '../../UI/instructor/onboarding/reviewSession/process';

import {
  coursesQuery,
  coursesSetupQuery,
  scenarioBulkAddCourse,
  scenarioLevelsQuery,
  studentCountQuery,
  tournamentsQuery,
  createTournaments,
  createGameFull
} from './api';

import {
  gameTypesReviewSession,
  businessTypes,
  nonCorporateTypes,
  scenarioLevelsDescription,
  gameCreateItemsQuestionsColumns,
  gameCreateItemsOptionsClassification,
  gameCreateItemsOptionsNormal
 } from './data';

import { getTargetEntityType, matchesEntityType, shuffleArray } from '../../../utils';

export default function InstructorOnboardingReviewSession() {
  const navigate = useNavigate();

  const { saveTournamentGames } = useAuth();

  const [courseId, setCourseId] = useState(null);

  const [selectedGameTypes, setSelectedGameTypes] = useState(
    gameTypesReviewSession.map(game => game.id)
  );
  
  const [selectedGameTypesMinutes, setSelectedGameTypesMinutes] = useState(30);
  const [activeLoadingStep, setActiveLoadingStep] = useState('');

  const { data: courses } = useQuery(coursesQuery());
  const { data: tournaments } = useQuery(tournamentsQuery('initial'));

  const { refetch: refetchCoursesSetup } = useQuery({
    ...coursesSetupQuery(),
    enabled: false
  });

  // Tiny count-only payload (vs paginating the full /course-members/
  // roster which can run into hundreds of KB on large courses).
  const { data: studentCount } = useQuery(studentCountQuery());

  const queryClient = useQueryClient();

  const refetchScenarioLevels = async (scenarioId) => {
    const data = await queryClient.fetchQuery({
      ...scenarioLevelsQuery({ scenarioId })
    });
    return data;
  };

  const scenarioBulkAddCourseMutation = useMutation({
    mutationFn: (data) => scenarioBulkAddCourse(data)
  });

  const createGameFullMutation = useMutation({
    mutationFn: (data) => createGameFull(data)
  });

  const createTournamentsMutation = useMutation({
    mutationFn: (data) => createTournaments(data)
  });

  const createTournament = async () => {
    const courseMembersCount = studentCount || 0;
    const amountOfGamesSelected = selectedGameTypes.length;
    let roundCount = 0;

    if(courseMembersCount < 4 || amountOfGamesSelected === 1) {
      roundCount = 1;
    } else if(courseMembersCount < 8 || amountOfGamesSelected === 2) {
      roundCount = 2;
    } else if(courseMembersCount < 16 || amountOfGamesSelected === 3) {
      roundCount = 3;
    } else if(courseMembersCount >= 16 || amountOfGamesSelected === 4) {
      roundCount = 4;
    }

    const existingCourseTournaments = tournaments?.filter(t => t.course_id === courseId) || [];
    const tournamentNumber = existingCourseTournaments.length + 1;

    await createTournamentsMutation.mutateAsync({
      name: `Accounting Basics Review ${tournamentNumber}`,
      courseId,
      roundCount
    }).catch((error) => {
      console.error('Error creating tournament:', error);
      return null;
    });

    navigate('/instructor/onboarding-review-session/all-set');
  }

  useEffect(() => {
    if(activeLoadingStep === selectedGameTypes?.length) {
      createTournament();
    }
  }, [activeLoadingStep, selectedGameTypes?.length]);
  
  useEffect(() => {
    if(courses?.length && tournaments) {
      const currentCourse = courses[0];
      if(tournaments.length >= currentCourse.game_limit) {
        navigate('/instructor/settings/assignments');
      }
    }
  }, [courses, tournaments]);

  const handleSelectGameType = ({ type }) => {
    setSelectedGameTypes(prevGameTypes => {
      let newGameTypes;
      
      if (prevGameTypes.includes(type)) {
        if (prevGameTypes.length > 1) {
          newGameTypes = prevGameTypes.filter(gameType => gameType !== type);
        } else {
          newGameTypes = prevGameTypes;
        }
      } else {
        newGameTypes = [...prevGameTypes, type];
      }
      
      const totalMinutes = newGameTypes.reduce((sum, gameType) => {
        const gameTypeData = gameTypesReviewSession.find(g => g.id === gameType);
        return sum + (gameTypeData?.minutes || 0);
      }, 0);
      
      setSelectedGameTypesMinutes(totalMinutes);
      
      return newGameTypes;
    });
  };

  const gameTypeMapping = {
    'classification': 'ACC',
    'normal': 'NOR',
    'equation': 'BE',
    'principles': 'PR',
  };

  // Build the payload for one level (questions + options arrays) without
  // making any API calls. The bulk create-full endpoint accepts these
  // inline. All callers now pass items pulled from the source template
  // (each carrying account_item.id / transaction_id / id), so the older
  // name -> id lookup against /games/items/ has been removed along with
  // its useDirectMapping flags.
  const buildLevelPayload = (gameType, config) => {
    const {
      name, levelNumber, description, questions, options,
      design, scoring, numDecisionsQuestion
    } = config;

    const questionFieldName = (gameType === 'equation' || gameType === 'principles')
      ? 'transaction'
      : 'account_item';

    const questionsPayload = (questions || []).map((question, idx) => ({
      order: question.order || idx + 1,
      [questionFieldName]: question.account_item?.id || question.transaction_id || question.id,
    })).filter(q => q[questionFieldName]);

    const levelPayload = {
      name,
      level: levelNumber,
      description,
      design,
      show_score: true,
      show_value: true,
      questions: questionsPayload,
      // Carry the source template's per-question scoring + question-count
      // config onto the new level so tournament-created assignments score
      // identically to the seeded templates. Only included when the source
      // actually defines them -- otherwise the backend's defaults apply.
      ...(scoring != null ? { scoring } : {}),
      ...(numDecisionsQuestion != null ? { num_decisions_question: numDecisionsQuestion } : {}),
    };

    // Options are now included for every game type. Principles APC
    // levels need them so the gameplay can validate clicks against
    // each option's account_item.id (without options the chips fall
    // back to a default name-only category list with null ids and
    // every decision is marked incorrect). Legacy principles levels
    // pass [] and are unaffected.
    const optionsPayload = (options || []).map((option, idx) => ({
      order: option.order || idx + 1,
      account_item: option.account_item?.id || option.id,
    })).filter(o => o.account_item);

    levelPayload.options = optionsPayload;

    return levelPayload;
  };

  // Build the entire levels array for a given game type, fetching any
  // template scenario data needed along the way. Aligned with the
  // single-source template structure used by the assignment editor:
  //   classification -> classification-game / classification-normal-template
  //   normal         -> normal-game         / classification-normal-template
  //   equation       -> be-game             (any level matching course
  //                                          entity_type, decision type
  //                                          accounts-impacted)
  //   principles     -> principles-game     (Tony's Slice of Heaven
  //                                          Pizzeria, fallback to any
  //                                          design=4 level)
  // The course's businesstype maps to an entity tag via
  // getTargetEntityType, and items / levels are filtered with
  // matchesEntityType so corporate / noncorporate variants are
  // surfaced according to the instructor's onboarding selection.
  const buildLevelsForGame = async (gameType, coursesSetupData, currentCourseBusinessType) => {
    const targetEntityType = getTargetEntityType(currentCourseBusinessType);
    const itemMatches = (q) =>
      matchesEntityType(q?.account_item?.entity_type, targetEntityType);
    const levelMatches = (l) =>
      matchesEntityType(l?.entity_type, targetEntityType);

    if (gameType === 'classification' || gameType === 'normal') {
      const sourceScenarioName =
        gameType === 'classification' ? 'classification-game' : 'normal-game';
      const sourceScenarioId = coursesSetupData.find(s => s.name === sourceScenarioName)?.id;
      if (!sourceScenarioId) return [];

      const scenarioLevels = await refetchScenarioLevels(sourceScenarioId);
      const template = scenarioLevels.find(l => l.name === 'classification-normal-template');
      if (!template) return [];

      const questionsKey = gameType === 'classification' ? 'questions' : 'normal_questions';
      const optionsKey = gameType === 'classification' ? 'options' : 'normal_options';
      const allTemplateItems = [
        ...(template[questionsKey] || []),
        ...(template[optionsKey] || []),
      ];

      // Level 1 -- the 5 financial-statement elements as questions and
      // the drop-zone options (Balance Sheet / Income Statement for
      // classification; Debit / Credit for normal). Match by element
      // name AND entity_type so corp / noncorp variants don't both
      // qualify for the instructor's selected business type.
      const level1Questions = gameCreateItemsQuestionsColumns
        .map(name => allTemplateItems.find(it =>
          it.account_item?.name === name && itemMatches(it)
        ))
        .filter(Boolean);

      const dropZoneNames = gameType === 'classification'
        ? gameCreateItemsOptionsClassification
        : gameCreateItemsOptionsNormal;
      const level1Options = dropZoneNames
        .map(name => (template[optionsKey] || []).find(opt =>
          opt.account_item?.name === name && itemMatches(opt)
        ))
        .filter(Boolean);

      const level1Config = {
        name: gameType === 'classification' ? 'class-elements' : 'norm-elements',
        levelNumber: 1,
        description: scenarioLevelsDescription[gameType][0],
        questions: level1Questions,
        options: level1Options,
        design: ['1'],
      };

      // Level 2 -- every question on the template that matches the
      // course's entity_type. Options match how the assignment-editor
      // builds level 2 today: classification re-uses the 5 elements
      // from the template's options (orders 3-7); normal balance uses
      // the Debit / Credit drop-zone constants.
      const level2Questions = (template[questionsKey] || []).filter(itemMatches);

      // Normal Balance pulls Debit / Credit from the template's
      // normal_options (same drop zones already used at level 1) so
      // we don't need a separate /games/items/ lookup. Classification
      // re-uses the 5 element options at orders 3-7.
      const level2Options = gameType === 'classification'
        ? (template.options || []).filter(opt =>
            opt.order >= 3 && opt.order <= 7 && itemMatches(opt)
          )
        : (template.normal_options || []).filter(itemMatches);

      const level2Config = {
        name: gameType === 'classification' ? 'class-default' : 'norm-default',
        levelNumber: 2,
        description: scenarioLevelsDescription[gameType][1] || scenarioLevelsDescription[gameType][0],
        questions: level2Questions,
        options: level2Options,
        design: ['2'],
      };

      return [
        buildLevelPayload(gameType, level1Config),
        buildLevelPayload(gameType, level2Config),
      ];
    }

    if (gameType === 'equation') {
      const sourceScenarioId = coursesSetupData.find(s => s.name === 'be-game')?.id;
      if (!sourceScenarioId) return [];

      const scenarioLevels = await refetchScenarioLevels(sourceScenarioId);

      // Pick any be-game level whose entity_type matches the course's
      // selected business type. One single Accounts Impacted level is
      // produced -- the simpler review-session form rather than the
      // 3-level Increase/Decrease -> Debit/Credit -> Accounts Impacted
      // staircase used elsewhere.
      const matchingLevel = scenarioLevels.find(levelMatches);
      if (!matchingLevel) return [];

      // Accounts-impacted options: every be_option whose underlying
      // account_item.account === true (matches the assignment editor's
      // design-3 rule).
      const accountsImpactedOptions = (matchingLevel.be_options || [])
        .filter(opt => opt.account_item?.account === true);

      return [buildLevelPayload(gameType, {
        name: 'accounts-impacted',
        levelNumber: 1,
        description: 'Decisions/Accounts Impacted',
        questions: matchingLevel.be_questions || [],
        options: accountsImpactedOptions,
        design: ['3'],
      })];
    }

    if (gameType === 'principles') {
      const sourceScenarioId = coursesSetupData.find(s => s.name === 'principles-game')?.id;
      if (!sourceScenarioId) return [];

      const scenarioLevels = await refetchScenarioLevels(sourceScenarioId);

      // Prefer Tony's Slice of Heaven Pizzeria; fall back to any
      // principles-game level whose design includes '4' (the
      // Assumptions, Principles, & Constraints decision type tag).
      // entity_type is checked first when picking, but if no
      // entity-matched candidate exists we relax to any matching
      // name / design rather than producing zero levels.
      const TONY_NAME = "Tony's Slice of Heaven Pizzeria";
      const isDesign4 = (l) => Array.isArray(l.design) && l.design.includes('4');

      let source =
        scenarioLevels.find(l => l.name === TONY_NAME && levelMatches(l))
        || scenarioLevels.find(l => l.name === TONY_NAME)
        || scenarioLevels.find(l => isDesign4(l) && levelMatches(l))
        || scenarioLevels.find(isDesign4);
      if (!source) return [];

      // APC: shuffle the transactions (mirrors the assignment-editor
      // save flow for assumptions-principles-constraints).
      return [
        buildLevelPayload(gameType, {
          name: 'assumptions-principles-constraints',
          levelNumber: 1,
          description: source.description,
          questions: shuffleArray(source.transactions || []),
          options: source.principles_options || [],
          design: ['4'],
        }),
      ];
    }

    return [];
  };

  const handleOnboardingComplete = async () => {
    try {
      const courseIds = courses.map(course => course.id);
      const courseId = courses[0]?.id;
      setCourseId(courseId);
      const currentCourse = courses[0];
      const currentCourseBusinessType = currentCourse?.businesstype;

      const { data: coursesSetupData } = await refetchCoursesSetup();

      if (coursesSetupData && coursesSetupData.length > 0) {
        await scenarioBulkAddCourseMutation.mutateAsync({
          scenarioIds: coursesSetupData.map(course => course.id),
          courseId: courseId
        });
      }

      saveTournamentGames([]);
      const tournamentGames = [];

      for (const [index, gameType] of selectedGameTypes.entries()) {
        const name = `Assignment #${index + 1}`;
        const description = gameTypesReviewSession.find(t => t.id === gameType).description;
        const startTime = moment().toISOString();
        const endTime = moment().add(30, 'days').toISOString();

        // Review Session onboarding now creates only the game + scenario
        // shell. The instructor is expected to build the scenario levels
        // themselves via the assignment editor after the tournament is
        // created, so the levels array is intentionally empty here.
        const payload = {
          game: {
            name,
            description,
            start_time: startTime,
            end_time: endTime,
            points: 100,
            is_active: true,
            game_type: gameTypeMapping[gameType],
            course: courseId,
          },
          scenario: {
            name,
            courses: courseIds,
            is_published: true,
          },
          levels: [],
        };

        const result = await createGameFullMutation.mutateAsync({ gameType, payload }).catch((error) => {
          console.error('Error creating game (bulk):', error);
          return null;
        });

        if (!result?.game?.id) {
          console.error('Failed to create game for type:', gameType);
          continue;
        }

        tournamentGames.push({
          id: result.game.id,
          type: result.game.game_type,
          courseId,
        });

        setActiveLoadingStep(index + 1);
      }

      saveTournamentGames(tournamentGames);

      // The tournament edit page reads games from the cached generalInfo
      // payload; without this invalidation, newly-created games are
      // missing from the list and rounds render with no game type label.
      queryClient.invalidateQueries({ queryKey: ['generalInfo'] });
    } catch (error) {
      console.error('Error in onboarding process:', error);
    }
  };

  return (
    <Layout
      isHeaderVisible={false}
      isContentVertical={true}
      backgroundColor={'linear-gradient(135deg, #EC5E59 2.88%, rgba(146, 219, 225, 0.8) 99.03%)'}
      className="instructor-onboarding-layout"
    >
      <ProcessReviewSession
        availableGameTypes={gameTypesReviewSession}
        onSelectGameType={handleSelectGameType}
        selectedGameTypes={selectedGameTypes}
        availableBusinessTypes={businessTypes}
        availableNonCorporateTypes={nonCorporateTypes}
        onFinish={handleOnboardingComplete}
        activeLoadingStep={activeLoadingStep}
        selectedGameTypesMinutes={selectedGameTypesMinutes}
      />
    </Layout>
  );
}
